import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Sparkles,
  Calendar,
  Pencil,
  Send,
  BarChart3,
  Mail,
  BookOpen,
  CircleHelp,
  Settings,
  CreditCard,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { useUIStore } from '@/lib/store';
import { useIsAdmin } from '@/features/auth/hooks';
import { Logo } from '@/components/brand/Logo';

/*
 * Sidebar
 *
 * Mockup: luviamo_app_mockup_v0_2_expanded_sidebar_avustaja.html
 *
 * Sektiot:
 * - Logo (Logo-komponentti, dotless-i + kultainen sun-piste)
 * - Päämoduulit: Etusivu + 5 elinkaarivaihetta (IDEOI/TUOTA/SUUNNITTELE/JULKAISE/SEURAA)
 *   Järjestys = työn kulku: ideoi -> tuota (hio valmiiksi) -> suunnittele (ajoita) ->
 *   julkaise -> seuraa. Ks. docs/design/content-pipeline-redesign.md (osa 1).
 * - Resurssit: Kirjasto
 * - Alhaalla: Asetukset + käyttäjäkortti (lisätään myöhemmin)
 *
 * Responsiivisuus (Sessio 4b, C4-paatos):
 * - Desktop (>=1024px / lg): kiinteä 220px sidebar, position: relative
 * - Mobiili (<1024px): fixed-overlay slide-in vasemmalta, hampurilaispainike
 *   Header:ssa avaa/sulkee. Backdrop sulkee myos. Linkin klikkaus sulkee.
 *
 * Aktiivinen reitti: bg-sammal + valkoinen teksti.
 * Hover (ei-aktiivinen): bg-ruoho.
 *
 * Ikonit (Sessio 4b, J6 korjattu): inline-SVG -> lucide-react.
 *
 * i18n: nav-otsikot ja moduulinimet luetaan common-namespacesta. Reittipolut
 * pidetaan yksikielisina (`/ideoi`, `/suunnittele`); siirto kaksikielisiin
 * tehdaan Vaihe 9:ssa kun kayttaja voi valita kielen.
 */

type NavItem = {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
};

const moduleNav: NavItem[] = [
  { to: '/', labelKey: 'sidebar.home', icon: Home, end: true },
  // Järjestys (Joanna 25.9.2026, Petri 30.9.): suunnitellaan ennen tuottamista, ja Viesti
  // (V6a1, yksi item + sisäiset välilehdet) kulkee julkaisun rinnalla ennen Seuraa-vaihetta.
  { to: '/ideoi', labelKey: 'sidebar.ideoi', icon: Sparkles },
  { to: '/suunnittele', labelKey: 'sidebar.suunnittele', icon: Calendar },
  { to: '/tuota', labelKey: 'sidebar.tuota', icon: Pencil },
  { to: '/julkaise', labelKey: 'sidebar.julkaise', icon: Send },
  { to: '/viesti', labelKey: 'sidebar.viesti', icon: Mail },
  { to: '/seuraa', labelKey: 'sidebar.seuraa', icon: BarChart3 },
];

const resourcesNav: NavItem[] = [
  { to: '/library', labelKey: 'sidebar.library', icon: BookOpen },
  // Käyttöopas (J16): /ohjeet — sisältö generoidaan docs/guide/*.md:stä
  { to: '/ohjeet', labelKey: 'sidebar.guide', icon: CircleHelp },
];

const settingsNav: NavItem[] = [
  // Palaute ensimmäisenä (Petrin päätös 20.8.2026): näkyvissä sivupalkissa Laskutuksen ja
  // Asetusten yläpuolella -- ei enää piilossa käyttäjävalikossa.
  { to: '/palaute', labelKey: 'sidebar.feedback', icon: MessageSquare },
  { to: '/billing', labelKey: 'sidebar.billing', icon: CreditCard },
  { to: '/settings', labelKey: 'sidebar.settings', icon: Settings },
];

export function Sidebar() {
  const { t } = useTranslation();
  const sidebarMobileOpen = useUIStore((s) => s.sidebarMobileOpen);
  const setSidebarMobileOpen = useUIStore((s) => s.setSidebarMobileOpen);
  // BUG-H0 (Joanna 12.8.2026): Laskutus vain owner/adminille — muut roolit eivät
  // näe linkkiä (route-guard BillingPagessa kattaa suoran URL:n).
  const isAdmin = useIsAdmin();
  const settingsItems = isAdmin ? settingsNav : settingsNav.filter((i) => i.to !== '/billing');

  // Linkin klikkaus mobiilissa sulkee drawer:n. Desktop:lla ei vaikutusta
  // (toggle on no-op kun drawer on jo "kiinni" mobile-kontekstissa).
  const handleLinkClick = () => setSidebarMobileOpen(false);

  return (
    <>
      {/* Backdrop mobiilissa kun drawer auki. Klikkaus sulkee. */}
      {sidebarMobileOpen && (
        <button
          type="button"
          aria-label={t('sidebar.closeBackdrop')}
          onClick={() => setSidebarMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={[
          'flex w-[var(--sidebar-width)] flex-col border-r border-kaste bg-valo',
          // Mobile: fixed overlay slide-in vasemmalta
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out',
          sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop (lg+): relative, ei transformia, aina nakyvissa
          'lg:relative lg:translate-x-0 lg:transition-none',
        ].join(' ')}
        // Sessio 18 a11y: kun drawer on auki mobiilissa, se on dialog (modal).
        // Desktop:lla sidebar on tavallinen complementary-rooli (oletus aside).
        // aria-hidden:ia EI aseteta -- desktop:lla sidebar on aina nakyvissa
        // ja transform-pohjainen mobile-drawer ei tarvitse aria-hidden:ia
        // (translateX(-100%) pois nakymasta). Lighthouse-audit Sessio 20:ssa
        // tarkistaa tablet-breakpoint:n + screen reader -kaytoksen.
        role={sidebarMobileOpen ? 'dialog' : undefined}
        aria-modal={sidebarMobileOpen ? true : undefined}
        aria-label={sidebarMobileOpen ? t('sidebar.navAriaLabel') : undefined}
      >
        <div className="px-5 pt-4 pb-3">
          <Logo className="text-xl" />
        </div>

        <nav
          className="flex-1 overflow-y-auto px-2.5 py-2"
          aria-label={t('sidebar.modules')}
        >
          <NavSection
            label={t('sidebar.modules')}
            items={moduleNav}
            onLinkClick={handleLinkClick}
          />
          <NavSection
            label={t('sidebar.resources')}
            items={resourcesNav}
            onLinkClick={handleLinkClick}
          />
        </nav>

        <div className="border-t border-kaste px-2.5 py-2">
          <NavSection label="" items={settingsItems} onLinkClick={handleLinkClick} />
        </div>
      </aside>
    </>
  );
}

function NavSection({
  label,
  items,
  onLinkClick,
}: {
  label: string;
  items: NavItem[];
  onLinkClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-3">
      {label && (
        <div className="px-2.5 pt-2 pb-1 text-xs font-medium uppercase tracking-wider text-lehti">
          {label}
        </div>
      )}
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onLinkClick}
              className={({ isActive }) =>
                [
                  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-base transition-colors focus-ring',
                  isActive
                    ? 'bg-sammal text-white'
                    : 'text-teksti hover:bg-ruoho',
                ].join(' ')
              }
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
